/*
 * File: SidebarNavItem.ts
 * Created: 2025-12-03 15:37:37
 * Author: Val Liu <valuis0429@gmail.com>
 * -----
 * Last Modified: 2026-02-05 20:08:32
 * Modified By: Val Liu <valuis0429@gmail.com>
 */

import { Component, Vue, Prop } from "vue-property-decorator";
// 假設這些工具函數的路徑與原專案相同，請根據實際情況調整
import { FindRouter } from "@/../core/router";
import { PermissionHelper } from "@root/core/utilities/permission-helper";
let config = require("@/config/default/debug");

// 定義傳入 data 的介面
interface IData {
    variant?: string;
    badge?: {
        label?: string;
        variant?: string;
    };
}

@Component
export class SidebarNavItem extends Vue {
    // --- Props 定義 ---
    @Prop({ type: String, required: false })
    label?: string;

    @Prop({ type: String, required: true })
    url!: string;

    @Prop({ type: String, required: false })
    icon?: string;

    @Prop({ type: Boolean, default: false })
    disabled!: boolean;

    // data prop 目前在樣式中較少用到，但先保留以兼容舊邏輯
    @Prop({ type: Object, required: false })
    data?: IData;

    // --- Getters (計算屬性) ---

    /**
     * 判斷是否為外部連結 (以 http 開頭)
     */
    get isExternalLink(): boolean {
        return Boolean(/^http/.test(this.url));
    }

    /**
     * 獲取顯示的標籤文字
     * 如果沒有提供 label prop，則嘗試從路由配置中獲取並翻譯
     */
    get computedLabel(): string {
        if (this.label) return this.label;
        let routers = FindRouter({ path: this.url });
        // 這裡假設 this.$_ 是一個全域的翻譯函數
        return routers.length === 0
            ? ""
            : (this as any).$_(routers[routers.length - 1].name);
    }

    /**
     * 計算圖示的 class 列表
     * 優先使用 icon prop，否則從路由配置中獲取
     */
    get iconClasses(): string[] {
        let classes = ["nav-icon", "mr-3"]; // mr-3 用於與文字保持間距
        let iconStr: string | undefined = this.icon;

        if (!iconStr) {
            let routers = FindRouter({ path: this.url });
            if (routers.length > 0) {
                iconStr = routers[routers.length - 1].icon;
            }
        }

        if (iconStr) {
            // 簡單判斷是否為特定圖示庫前綴
            if (/^fa|^fas|^far|^fab/.test(iconStr)) classes.push("fa");
            if (/^isap/.test(iconStr)) classes.push("isap-icon");
            classes.push(iconStr);
        }

        return classes;
    }

    /**
     * 檢查是否因權限不足而被阻擋
     */
    get isBlocked(): boolean {
        if (this.isExternalLink) return false;
        let routers = FindRouter({ path: this.url });
        if (routers.length === 0) return false;
        let router = routers[routers.length - 1];

        // 使用權限輔助工具檢查
        // 注意：這裡需要確保 PermissionHelper.authRouter 能正確綁定 this 上下文
        return !PermissionHelper.authRouter.call(this, router);
    }

    /**
     * 檢查路由是否無效
     */
    get isInvalid(): boolean {
        if (this.isExternalLink) return false;
        let routers = FindRouter({ path: this.url });
        return routers.length === 0;
    }

    /**
     * 計算外層 li 元素的動態 class
     * 主要處理權限阻擋和無效狀態的樣式
     */
    get itemClasses(): any {
        const isProd = Boolean(config.prodMode);
        const showDebugPlaceholder = !isProd && Boolean(config.showNonePermissionBlock);
        return {
            // "is-disabled": follow prop disabled
            "is-disabled": this.disabled,
            // "blocked-prod": production mode + permission blocked => hide
            "blocked-prod": this.isBlocked && (isProd || !showDebugPlaceholder),
            // "invalid-prod": production mode + invalid routing => hide
            "invalid-prod": this.isInvalid && (isProd || !showDebugPlaceholder),
            // "blocked-dev": development mode + no placeholder => hide
            "blocked-dev": this.isBlocked && !isProd && !showDebugPlaceholder,
            // "invalid-dev": development mode + no placeholder => hide
            "invalid-dev": this.isInvalid && !isProd && !showDebugPlaceholder,
            // "blocked": development mode + showNonePermissionBlock => show placeholder
            blocked: this.isBlocked && !isProd && showDebugPlaceholder,
            // "invalid": development mode + showNonePermissionBlock => show placeholder
            invalid: this.isInvalid && !isProd && showDebugPlaceholder,
        };
    }

    get isHidden(): boolean {
        const isProd = Boolean(config.prodMode);
        const showDebugPlaceholder = !isProd && Boolean(config.showNonePermissionBlock);
        const classes = this.itemClasses || {};

        // Production: hide blocked/invalid items.
        if (isProd) return Boolean(classes["blocked-prod"] || classes["invalid-prod"]);

        // Development: if showing debug placeholders, never hide.
        if (showDebugPlaceholder) return false;

        // Development: otherwise hide blocked/invalid.
        return Boolean(
            classes["blocked-dev"] ||
                classes["invalid-dev"] ||
                classes["blocked-prod"] ||
                classes["invalid-prod"]
        );
    }
}
export default SidebarNavItem;
